import { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { type RoleSummary } from '@/pages/company/roles/rolesLogic';
import { useDeleteRole } from '@/pages/company/roles/hooks/useRolesQueries';

interface DeleteRoleDialogProps {
  role: RoleSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * DeleteRoleDialog — удаление кастомной роли (краевой случай, фрейм 7).
 *
 * Нельзя удалить роль с активными участниками: если member_count > 0 —
 * кнопка удаления задизейблена, показываем «сначала переведите их на другую
 * роль» (в списке команды). Бэк дополнительно вернёт 409 при гонке —
 * показываем тостом.
 */
export function DeleteRoleDialog({ role, open, onOpenChange }: DeleteRoleDialogProps) {
  const deleteMut = useDeleteRole();
  const [error, setError] = useState<string | undefined>();

  if (!role) return null;

  const hasMembers = role.member_count > 0;

  const submit = async () => {
    setError(undefined);
    try {
      await deleteMut.mutateAsync(role.id);
      toast.success(`Роль «${role.name}» удалена`);
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Не удалось удалить роль';
      if (e instanceof ApiError && e.status === 409) {
        setError(msg);
      } else {
        setError(msg);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[430px]">
        <DialogHeader>
          <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <Trash2 className="h-5 w-5" />
          </span>
          <DialogTitle>Удалить роль «{role.name}»?</DialogTitle>
          <DialogDescription>
            {hasMembers
              ? 'На этой роли есть участники — сначала переведите их на другую роль.'
              : 'Роль будет удалена без возможности восстановления. Права по этой роли перестанут действовать.'}
          </DialogDescription>
        </DialogHeader>

        {hasMembers && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              На роли <b>{role.member_count}</b> активных участник(ов). Переведите их на
              другую роль во вкладке «Команда», затем удалите роль.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={deleteMut.isPending}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={hasMembers || deleteMut.isPending}
          >
            {deleteMut.isPending ? 'Удаляем…' : 'Удалить роль'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
